import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BasicWebkSubject, ObservableComponent } from 'ngx-esprio-shared';
import { AuthSelectors } from 'projects/webk/src/app/core/auth/selectors/auth.selectors';
import { BaseAppState } from 'projects/webk/src/app/core/store/reducers';
import { UserDataApiActions } from 'projects/webk/src/app/core/users/actions/user-data-api.actions';
import { UserDataSelectors } from 'projects/webk/src/app/core/users/selectors/user-data.selectors';
import { UserListItem } from 'projects/webk/src/app/shared/models';
import { BehaviorSubject, combineLatest, filter, map, takeUntil } from 'rxjs';

@Component({
  selector: 'app-monitored-user-analysis',
  templateUrl: './monitored-user-analysis.component.html',
  styleUrls: ['./monitored-user-analysis.component.scss'],
})
export class MonitoredUserAnalysisComponent
  extends ObservableComponent
  implements OnInit, OnChanges
{
  @ViewChild('webkSection') webkSection!: ElementRef<HTMLDivElement>;

  @Input() user: UserListItem | undefined;
  private user$ = new BehaviorSubject<UserListItem | undefined>(undefined);

  public date$ = new BehaviorSubject<Date>(moment().startOf('month').toDate());
  public visibleSubjects: BasicWebkSubject[] | null = null;

  public weightData$ = this.store.select(UserDataSelectors.getWeightHistoryEntries);
  public basicWebkData$ = this.store.select(UserDataSelectors.getBasicWebkHistoryEntries);
  public weightChangePercentage$ = this.store.select(UserDataSelectors.getWeightChangePercentage);
  public timeSnap$ = this.store.select(UserDataSelectors.getTimeSnap);
  public scoreDetails$ = this.store.select(UserDataSelectors.getScoreDetails);
  public latestSubmissionDetails$ = this.store.select(UserDataSelectors.getLatestSubmissionDetails);

  private loggedInUser$ = this.store.select(AuthSelectors.getLoggedInUser);

  constructor(private store: Store<BaseAppState>) {
    super();
  }

  ngOnInit(): void {
    combineLatest([this.loggedInUser$, this.user$, this.date$])
      .pipe(
        filter(([loggedIn, user, date]) => !!loggedIn && !!user && !!date),
        takeUntil(this.ngDestroy$)
      )
      .subscribe(([loggedIn, user, date]) => {
        this.fetchUserData(loggedIn!, user!, date!);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['user']) {
      this.user$.next(this.user);
    }
  }

  private fetchUserData(loggedInUser, selectedUser, _date) {
    const selectedDate = this.date$.value;
    const startDate = moment(selectedDate).startOf('month').toDate();
    const endDate = moment(selectedDate).endOf('month').toDate();

    this.store.dispatch(
      UserDataApiActions.getUserWeightHistoryData({
        userId: loggedInUser.id,
        monitoredUserId: selectedUser.id,
        startDate,
        endDate,
      })
    );
    this.store.dispatch(
      UserDataApiActions.getUserBasicWebkData({
        userId: loggedInUser.id,
        monitoredUserId: selectedUser.id,
        startDate,
        endDate,
      })
    );
    this.store.dispatch(
      UserDataApiActions.getUserWeightChangePercentage({
        userId: loggedInUser.id,
        monitoredUserId: selectedUser.id,
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
      })
    );
    this.store.dispatch(
      UserDataApiActions.getUserTimeSnap({
        userId: loggedInUser.id,
        monitoredUserId: selectedUser.id,
      })
    );
  }

  public bmi$ = combineLatest([this.timeSnap$, this.user$]).pipe(
    filter(([timeSnap, user]) => !!timeSnap && !!user?.coreData?.bodySizeInCm),
    map(([timeSnap, user]) => {
      const heightInM = user!.coreData!.bodySizeInCm / 100;
      const bmi = Math.floor(timeSnap!.weight / (heightInM * heightInM));
      return bmi;
    })
  );

  public vm$ = combineLatest([
    this.user$,
    this.bmi$,
    this.weightChangePercentage$,
    this.timeSnap$,
    this.scoreDetails$,
    this.latestSubmissionDetails$,
  ]).pipe(
    map(([user, bmi, weightChangePercentage, timeSnap, scoreDetails, latestSubmissionDetails]) => ({
      user,
      bmi,
      weightChangePercentage: weightChangePercentage?.weightChangePercentage,
      timeSnap,
      scoreDetails,
      latestSubmissionDetails,
      date: this.date$.value,
    }))
  );

  onDateChange(date: Date) {
    this.date$.next(date);
  }

  public onVisibleChange(subjects: BasicWebkSubject[]): void {
    this.visibleSubjects = subjects;

    setTimeout(() => {
      this.webkSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }
}
